import { FormEvent, useEffect, useState } from 'react';
import { Img } from 'react-image';
import classNames from 'classnames';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import FloatingCompany from '../shared/FloatingCompany';
import { sendOtp } from 'src/apis/user';
import { useMutation } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import EnterPhoneOrEmail from './EnterPhoneOrEmail';
import { get } from 'lodash';
import PhoneOrEmailVerification from './PhoneOrEmailVerification';
import { isPhoneOrEmail } from 'shared-utils';
import { sendTrackingEvent } from 'src/utils/gtm';
import {
	WEBSITE_URL,
	isRecruiterSide,
	STUDENT_APP_ENDPOINT,
	RECRUITER_APP_ENDPOINT,
	UNIVERSITY_APP_ENDPOINT,
	isUniversitySide,
} from 'src/config/env.config';
import { useThemeContext } from 'src/context/ThemeProvider';
import { recordFETracking } from 'src/apis/tracking';
import { TrackingType } from 'types';
import AuthSelector from './AuthSelector';

const SignInForm = ({ showFormUiOnly = false }: { showFormUiOnly?: boolean }) => {
	const location = useLocation();
	const { user, tokenLoginErrorMessage, setTokenLoginErrorMessage } = useUserContext();
	const [searchParamas] = useSearchParams();
	const toast = useToast();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [emailOrPhone, setEmailOrPhone] = useState<string>('prefilledEmail');
	const [otpSubmitted, setOtpSubmitted] = useState<boolean>(false);
	const [changeOtpMethod, setChangeOtpMethod] = useState(false);
	const redirectUrl = searchParamas.get('redirect_url');
	const prefilledEmail = searchParamas.get('email');

	const generateVerificationCodeFn = useMutation({
		mutationKey: 'sendOtp',
		mutationFn: sendOtp,
		onSuccess() {
			const type = isPhoneOrEmail(emailOrPhone);
			toast({
				type: 'success',
				title: `An OTP has sent to your registered ${type}.`,
			});
			setIsLoading(false);
			setOtpSubmitted(true);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
		},
	});

	const recordFETrackingFn = useMutation(['recordFETracking'], recordFETracking, {
		onSuccess() {
			console.log('success tracking');
		},
		onError() {
			console.log('error');
		},
	});

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		if (isRecruiterSide) {
			sendTrackingEvent({
				event: 'recr_signin_continue',
				eventLabel: location.pathname,
			});
		} else {
			sendTrackingEvent({
				event: 'sign_in_continue',
				eventLabel: location.pathname,
			});
		}
		event.preventDefault();
		const verificationPayload = {
			emailOrPhone: emailOrPhone.trim().toLowerCase(),
		};

		const phoneRegex = /^[0-9]{10}$/;
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

		if (!phoneRegex.test(emailOrPhone.trim().toLowerCase()) && !emailRegex.test(emailOrPhone.trim().toLowerCase())) {
			toast({
				type: 'error',
				title: `Phone number or email is not valid!`,
			});
			return;
		}

		setIsLoading(true);
		setChangeOtpMethod(false);
		generateVerificationCodeFn.mutate(verificationPayload);
	};

	useEffect(() => {
		if (user) {
			if (redirectUrl) {
				window.open(decodeURIComponent(redirectUrl), '_self');
			} else {
				// navigate('/dashboard');
				navigate('/my-applications');
			}
		}
	});

	// Show toast for LMS token login errors (set by LayoutProvider when token is invalid / no Levelup account / etc.)
	useEffect(() => {
		if (tokenLoginErrorMessage) {
			toast({ title: tokenLoginErrorMessage, type: 'error' });
			setTokenLoginErrorMessage(null);
		}
	}, [tokenLoginErrorMessage, toast, setTokenLoginErrorMessage]);

	useEffect(() => {
		setEmailOrPhone('');
		setOtpSubmitted(false);
		setChangeOtpMethod(false);
	}, []);

	useEffect(() => {
		if (prefilledEmail && prefilledEmail !== 'undefined') {
			if (!recordFETrackingFn.isLoading)
				recordFETrackingFn.mutate({
					type: TrackingType.FLYWHEEL_LEAD_LANDED_ON_SIGN_IN,
					createdAt: new Date(),
					meta: {
						email: prefilledEmail,
						url: window.location.href,
					},
				});
			setEmailOrPhone(prefilledEmail);
		}
	}, [prefilledEmail]);

	const { themeMode } = useThemeContext();

	return (
		<div
			style={
				showFormUiOnly
					? {}
					: {
						background: `url(${themeMode === 'dark' ? '/assets/app-bg.svg' : '/assets/desktop-bg.svg'}) no-repeat`,
					}
			}
			className={
				'h-[100%] flex-1 w-full flex flex-col min-w-[360px] md:min-w-[400px] justify-start md:justify-around items-center relative bg-no-repeat !bg-cover md:pt-[60px] md:!bg-[#E5F0FF]'
			}
		>
			{showFormUiOnly ? null : (
				<div className={'max-w-lg lg:w-full pl-[12px] block w-[100%] mb-[auto] md:mb-[0px]'}>
					<Img
						loading="lazy"
						className={'block md:hidden pt-[20px] pb-[18px] pl-[6px]'}
						src={
							themeMode === 'dark'
								? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
								: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
						}
						width="101"
						height="36"
						alt="logo"
					/>

					{isUniversitySide ? (
						<p
							className={
								'whitespace-pre-wrap text-[32px] lg:text-[44px] leading-[40px] font-inter font-[700] dark:text-white text-[#1F3343] md:hidden'
							}
						>
							Identify{' '}
							<span className="dark:text-[#01b899] bg-dark-gradient-skyblue bg-clip-text text-transparent">
								{' '}
								job-ready students
							</span>{' '}
							with precision
						</p>
					) : isRecruiterSide ? (
						<p
							className={
								'whitespace-pre-wrap text-[32px] lg:text-[44px] leading-[40px] font-inter font-[700] dark:text-white text-[#1F3343] md:hidden'
							}
						>
							Match with the{' '}
							<span className="dark:text-[#01b899] bg-dark-gradient-skyblue bg-clip-text text-transparent">
								Tech <br />
								Talent
							</span>{' '}
							you need in <br />
							<span>minutes</span>
						</p>
					) : (
						<p
							className={
								'whitespace-pre-wrap text-[32px] lg:text-[44px] leading-[40px] font-inter font-[700] text-[#1F3343] dark:text-white md:hidden'
							}
						>
							Ready to get your dream <span className="text-[#01b899]">job</span>
						</p>
					)}
					<div className={classNames('block pt-2 text-base font-medium dark!text-[#C7C9D9] text-[#555770] md:hidden')}>
						{isRecruiterSide ? (
							<div>Seeking top-tier, assessed tech talent for your team? Look no further.</div>
						) : !otpSubmitted ? (
							<div>Sign up now and start your job seeking journey with our help.</div>
						) : (
							'Get your dream tech job'
						)}
					</div>

					{!isRecruiterSide ? (
						<div className="md:hidden py-2">
							<FloatingCompany />
						</div>
					) : null}

					<div
						className="signIn_logo_div cursor-pointer"
						onClick={() => {
							isRecruiterSide ? window.open(`${WEBSITE_URL}/hire-from-us`) : window.open(`${WEBSITE_URL}/`);
						}}
					>
						<Img
							loading="lazy"
							src={
								themeMode === 'dark'
									? 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
									: 'https://masai-website-images.s3.ap-south-1.amazonaws.com/level_Up_By_Masai_Icon_Dark_a5f53496b9.svg'
							}
							alt="logo"
							width="93"
							height="24"
						/>
					</div>
				</div>
			)}

			<div
				className={classNames(
					'text-[#28293D] dark:text-white rounded-[48px] md:mb-[-20%] w-full md:w-max',
					isRecruiterSide ? 'mt-[10px]' : '',
				)}
			>
				<AuthSelector
					value={
						isUniversitySide
							? {
								label: 'University',
								value: 'University',
								description: (
									<>
										Assess <br /> Students
									</>
								),
							}
							: isRecruiterSide
								? {
									label: 'Recruiter',
									value: 'Recruiter',
									description: (
										<>
											Find <br /> Jobs
										</>
									),
								}
								: {
									label: 'Jobseeker',
									value: 'Jobseeker',
									description: (
										<>
											Hire <br /> Talent
										</>
									),
								}
					}
					options={[
						{
							label: 'Jobseeker',
							value: 'Jobseeker',
							description: (
								<>
									Find <br /> Jobs
								</>
							),
						},
						{
							label: 'Recruiter',
							value: 'Recruiter',
							description: (
								<>
									Hire <br /> Talent
								</>
							),
						},
						{
							label: 'University',
							value: 'University',
							description: (
								<>
									Assess <br /> Students
								</>
							),
						},
					]}
					onChange={data => {
						// presserve search params
						const searchParams = new URLSearchParams(window.location.search);
						const params = new URLSearchParams();
						searchParams.forEach((value, key) => {
							params.append(key, value);
						});
						console.log('here data:', data, isUniversitySide, UNIVERSITY_APP_ENDPOINT);
						if (data.value === 'Jobseeker' && (isUniversitySide || isRecruiterSide)) {
							window.open(`${STUDENT_APP_ENDPOINT}/auth/signin?${params.toString()}`, '_self');
						} else if (data.value === 'Recruiter' && !isRecruiterSide) {
							window.open(`${RECRUITER_APP_ENDPOINT}/auth/signin?${params.toString()}`, '_self');
						} else if (data.value === 'University' && !isUniversitySide) {
							console.log(
								'inside here data:',
								data,
								isUniversitySide,
								UNIVERSITY_APP_ENDPOINT,
								`${UNIVERSITY_APP_ENDPOINT}/auth/signin?${params.toString()}`,
							);
							window.open(`${UNIVERSITY_APP_ENDPOINT}/auth/signin?${params.toString()}`, '_self');
						}
					}}
					label="Logging in as:"
				/>
			</div>

			{!otpSubmitted || changeOtpMethod ? (
				<EnterPhoneOrEmail
					emailOrPhone={emailOrPhone}
					setEmailOrPhone={setEmailOrPhone}
					isLoading={isLoading}
					handleSubmit={handleSubmit}
				/>
			) : (
				<PhoneOrEmailVerification
					emailORPhone={emailOrPhone}
					setEmailOrPhone={setEmailOrPhone}
					setOtpSubmitted={setOtpSubmitted}
					btnText={'Sign In'}
					changeOtpMethod={changeOtpMethod}
					setChangeOtpMethod={setChangeOtpMethod}
				/>
			)}
		</div>
	);
};

export default SignInForm;
